import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Grow from '@mui/material/Grow';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Consumer from '../partials/Consumer';
import { useFlow } from '../context/FlowContext';
import { useI18N } from '../context/I18NContext';
import { useSettings } from '../context/SettingsContext';
import useWidgetStore from '../store/useWidgetStore';
import { isMobile } from '../../../helpers/browser';
const WidgetWrapper = ({ children }) => {
    const theme = useTheme();
    const { currentRoute } = useFlow();
    const { consumer, language } = useWidgetStore();
    const { t } = useI18N();
    const { widgetMode, orientation, defaultOpened, hasAccounts } = useSettings();
    const [showUser, setShowUser] = useState(false);
    const [open, setOpen] = useState(false);
    const [close, setClose] = useState(true);
    // useEffect(() => {
    //     // show the consumer bar if there is a consumer, or if we need to show the language select
    //     setShowUser(hasAccounts && !!consumer?.id)
    // }, [currentRoute])
    //const isDesktop = Math.min(window.screen.width, window.innerWidth) > 500
    const isDesktop = window.screen.width > 500;
    const openWidget = () => {
        if (['triggerButtonLeft', 'triggerButtonRight'].includes(widgetMode) || (widgetMode === 'default' && !isDesktop)) {
            if (['triggerButtonLeft', 'triggerButtonRight'].includes(widgetMode)) {
                window.parent.postMessage(Object.assign(Object.assign({ type: 'setDimensions', width: isDesktop ? '520px' : '100dvw', height: isDesktop ? "min(800px, calc(100dvh - 64px))" : '100dvh', bottom: isDesktop ? '32px' : '0' }, (widgetMode === 'triggerButtonLeft' && ({
                    left: isDesktop ? '32px' : '0'
                }))), (widgetMode === 'triggerButtonRight' && ({
                    right: isDesktop ? '32px' : '0'
                }))), '*');
            }
            else if (widgetMode === 'default' && !isDesktop) {
                window.parent.postMessage({
                    type: 'setDimensions',
                    width: '100dvw',
                    height: '100vh',
                    bottom: '0',
                    left: '0',
                    top: '0',
                    position: 'fixed'
                }, '*');
            }
        }
        setClose(false);
        setOpen(true);
    };
    const closeWidget = () => {
        setOpen(false);
        if (widgetMode === 'default' && !isDesktop) {
            // first resize to the a small frame that fits the whole button ( and we're too wide )
            window.parent.postMessage({
                type: 'setDimensions',
                width: '400px',
                height: '54px',
                position: 'relative'
            }, '*');
            setTimeout(() => {
                setClose(true);
                resizeTriggerButton();
            }, 300);
        }
        else {
            if (['triggerButtonLeft', 'triggerButtonRight'].includes(widgetMode) && !isDesktop) {
                // this is a full-screen mobile widget, resize to the offset border of the viewport first before
                // we start the animation
                window.parent.postMessage({
                    type: 'setDimensions',
                    width: isDesktop ? '520px' : '100dvw',
                    height: '100dvh',
                    bottom: '10px',
                    left: widgetMode === 'triggerButtonLeft' ? '10px' : 0,
                    right: widgetMode === 'triggerButtonRight' ? '10px' : 0
                }, '*');
            }
            setTimeout(() => {
                setClose(true); // after the timeout, so the animation can finish
                resizeTriggerButton();
            }, 300);
        }
    };
    const resizeTriggerButton = () => {
        let _button = document.getElementById('trigger-button');
        if (_button) {
            // 1px extra was enough to fix the rounding issue after closing, but just to make sure
            let _width = _button ? `${_button.offsetWidth + 10}px` : '200px';
            if (widgetMode === 'default' && !isDesktop) {
                window.parent.postMessage({
                    type: 'setDimensions',
                    width: _width,
                    height: '54px',
                    position: 'relative'
                }, '*');
            }
            if (['triggerButtonLeft', 'triggerButtonRight'].includes(widgetMode)) {
                window.parent.postMessage({
                    type: 'setDimensions',
                    width: _width,
                    height: '54px',
                    bottom: isDesktop ? '32px' : '10px',
                    left: widgetMode === 'triggerButtonLeft' ? (isDesktop ? '32px' : '10px') : 0,
                    right: widgetMode === 'triggerButtonRight' ? (isDesktop ? '32px' : '10px') : 0
                }, '*');
            }
        }
        else {
            setTimeout(() => {
                resizeTriggerButton();
            }, 100);
        }
    };
    const innerWrapper = (_jsxs(_Fragment, { children: [children, showUser && (_jsx(Paper, { sx: {
                    backgroundColor: theme.palette.background.default,
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: isDesktop ? 'calc(100% - 16px)' : 'calc(100dvw - 16px)',
                    px: 1,
                    minHeight: '60px',
                    zIndex: 2,
                    borderColor: theme.palette.divider,
                    borderWidth: '1px 0 0 0',
                    borderStyle: "solid"
                }, elevation: 0, children: _jsx(Consumer, {}) }))] }));
    return (_jsx(Fragment, { children: _jsxs(_Fragment, { children: [widgetMode === "triggerButtonLeft" && (_jsx(_Fragment, { children: _jsxs(Box, { sx: Object.assign({ position: 'fixed', bottom: '0', left: '0' }, (!isDesktop && {
                            width: '100%',
                            padding: 0
                        })), children: [_jsx(Grow, { style: {
                                    transformOrigin: 'bottom left'
                                }, in: open, children: _jsxs(Paper, { elevation: 4, sx: {
                                        position: 'relative',
                                        width: isDesktop ? '500px' : '100vw',
                                        height: isDesktop ? "calc(100dvh - 16px)" : "100dvh",
                                        marginBottom: isDesktop ? '8px' : 0,
                                        minHeight: '550px',
                                        borderRadius: isDesktop ? '16px' : 0,
                                        overflow: 'hidden'
                                    }, children: [innerWrapper, _jsx(IconButton, { onClick: closeWidget, sx: {
                                                color: theme.palette.text.primary,
                                                position: 'absolute',
                                                top: '4px',
                                                right: '16px',
                                                zIndex: 1000
                                            }, children: _jsx(CloseIcon, {}) })] }) }), close && (_jsx(Button, { id: "trigger-button", sx: {
                                    fontSize: '1rem',
                                    paddingX: '1.5em',
                                    borderRadius: '8px'
                                }, variant: "contained", onClick: openWidget, children: t('Maak afspraak', 'select1') }))] }) })), widgetMode === "triggerButtonRight" && (_jsx(_Fragment, { children: _jsxs(Box, { sx: Object.assign({ position: 'fixed', bottom: '0', right: '0' }, (!isDesktop && {
                            width: '100%',
                            padding: 0
                        })), children: [_jsx(Grow, { style: {
                                    transformOrigin: 'bottom right'
                                }, in: open, children: _jsxs(Paper, { elevation: 4, sx: {
                                        position: 'relative',
                                        width: isDesktop ? '500px' : '100vw',
                                        height: isDesktop ? "calc(100dvh - 16px)" : "100dvh",
                                        marginBottom: isDesktop ? '8px' : 0,
                                        minHeight: '550px',
                                        borderRadius: isDesktop ? '16px' : 0,
                                        overflow: 'hidden'
                                    }, children: [innerWrapper, _jsx(IconButton, { onClick: closeWidget, sx: {
                                                color: theme.palette.text.primary,
                                                position: 'absolute',
                                                top: '4px',
                                                right: '16px',
                                                zIndex: 1000
                                            }, children: _jsx(CloseIcon, {}) })] }) }), close && (_jsx(Button, { id: "trigger-button", sx: {
                                    fontSize: '1rem',
                                    paddingX: '1.5em',
                                    borderRadius: '8px',
                                    position: 'absolute',
                                    right: 0,
                                    bottom: 0
                                }, variant: "contained", onClick: openWidget, children: t('Maak afspraak', 'select1') }))] }) })), widgetMode === "default" && (_jsxs(_Fragment, { children: [isDesktop && (_jsx(Box, { sx: {
                                width: orientation === "horizontal" ? 'calc(100% - 16px)' : '500px',
                                paddingTop: '10px',
                                marginX: '10px'
                            }, children: _jsx(Paper, { elevation: 4, sx: {
                                    position: 'relative',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    height: 'calc(100dvh - 16px)' // this makes sure the widget always has the same height, and the bottom bar is fixed relative to that
                                }, children: innerWrapper }) })), !isDesktop && (_jsxs(_Fragment, { children: [defaultOpened.mobile && (_jsx(Box, { sx: {
                                        width: '100vw'
                                    }, children: _jsx(Paper, { elevation: 4, sx: {
                                            position: 'relative',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            height: '100dvh'
                                        }, children: innerWrapper }) })), !defaultOpened.mobile && (_jsxs(Box, { sx: {
                                        width: '100%',
                                        padding: 0
                                    }, children: [open && (_jsxs(Paper, { elevation: 4, sx: {
                                                position: 'relative',
                                                width: '100vw',
                                                height: "100vh",
                                                marginBottom: 0,
                                                minHeight: '550px',
                                                borderRadius: 0
                                            }, children: [innerWrapper, _jsx(IconButton, { onClick: closeWidget, sx: {
                                                        color: theme.palette.text.primary,
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '16px',
                                                        zIndex: 1000
                                                    }, children: _jsx(CloseIcon, {}) })] })), close && (_jsx(Button, { id: "trigger-button", sx: {
                                                fontSize: '1rem',
                                                paddingX: '1.5em',
                                                borderRadius: '8px'
                                            }, variant: "contained", onClick: openWidget, children: t('Maak afspraak', 'select1') }))] }))] }))] })), widgetMode === "standalone" && (_jsxs(_Fragment, { children: [isDesktop && (_jsx(Box, { sx: {
                                width: orientation === "horizontal" ? 'calc(100% - 32px)' : '500px',
                                margin: '0 auto',
                                paddingY: orientation === "horizontal" ? '16px' : '60px'
                            }, children: _jsx(Paper, { elevation: 4, sx: Object.assign(Object.assign({ position: 'relative', borderRadius: '16px', 
                                    // Content sized off the raw viewport (not this card's own width) can be
                                    // wider than the card - horizontal desktop was the only combination
                                    // left unclipped, which let that overflow inflate the page's scrollable
                                    // width and made the margin:auto centering above look off (the card
                                    // pinned to the left, page background filling the right).
                                    overflow: 'hidden' }, (orientation === 'horizontal' && isMobile && {
                                    height: 'min(1080px,  calc(100vh - 120px))' // this makes sure the widget always has the same height, and the bottom bar is fixed relative to that
                                })), (orientation === 'vertical' && {
                                    height: 'min(1080px,  calc(100vh - 120px))' // this makes sure the widget always has the same height, and the bottom bar is fixed relative to that
                                })), children: innerWrapper }) })), !isDesktop && (_jsx(Box, { sx: {
                                width: '100%',
                                height: '100dvh',
                                position: 'relative',
                                borderColor: theme.palette.mode === 'dark' ? '#333' : '#efefef',
                                borderStyle: 'solid',
                                borderRightWidth: '1px'
                            }, children: innerWrapper }))] })
                // <Box sx={{
                //     width: '500px',
                //     margin: '0 auto',
                //     paddingY: '60px'
                // }}>
                //     <Paper elevation={4} sx={{
                //         position: 'relative',
                //         borderRadius: '16px',
                //         overflow: 'hidden',
                //         height: 'calc(100vh - 120px)' // this makes sure the widget always has the same height, and the bottom bar is fixed relative to that
                //     }}>
                //         {innerWrapper}
                //     </Paper>
                // </Box>
                )] }) }, language));
};
export default WidgetWrapper;
